import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'

export interface ThermalPrinterSettings {
  defaultPrinter: string
  paperSize: '80mm' | '58mm' | '44mm'
  enablePreview: boolean
  copies: number
  charactersPerLine?: number
  printerType?: string
}

interface BasicPrinterSettingsProps {
  isOpen: boolean
  onClose: () => void
  onSettingsChange?: (settings: ThermalPrinterSettings) => void
}

export const BasicPrinterSettings: React.FC<BasicPrinterSettingsProps> = ({
  isOpen,
  onClose,
  onSettingsChange
}) => {
  // Load saved settings from localStorage
  const [settings, setSettings] = useState<ThermalPrinterSettings>(() => {
    const savedSettings = localStorage.getItem('thermalPrinterSettings')
    if (savedSettings) {
      return JSON.parse(savedSettings)
    }
    return {
      defaultPrinter: '',
      paperSize: '80mm',
      enablePreview: true,
      copies: 1,
      charactersPerLine: 40,
      printerType: 'generic80mm'
    }
  })

  const handleSave = () => {
    // Save to localStorage
    localStorage.setItem('thermalPrinterSettings', JSON.stringify(settings))
    
    if (onSettingsChange) {
      onSettingsChange(settings)
    }
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Thermal Printer Settings</DialogTitle>
          <DialogDescription>
            Configure your thermal printer settings
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Printer Type */}
          <div className="grid gap-2">
            <Label>Printer Type</Label>
            <Select
              value={settings.printerType || 'generic80mm'}
              onValueChange={(value) => {
                const printerConfigs: Record<string, { charactersPerLine: number, paperSize: '80mm' | '58mm' | '44mm' }> = {
                  'generic80mm': { charactersPerLine: 40, paperSize: '80mm' },
                  'epson80mm': { charactersPerLine: 42, paperSize: '80mm' },
                  'star80mm': { charactersPerLine: 48, paperSize: '80mm' },
                  'compact80mm': { charactersPerLine: 38, paperSize: '80mm' },
                  'narrow80mm': { charactersPerLine: 36, paperSize: '80mm' },
                  'generic58mm': { charactersPerLine: 32, paperSize: '58mm' }
                }
                const config = printerConfigs[value]
                setSettings({ 
                  ...settings, 
                  printerType: value,
                  charactersPerLine: config.charactersPerLine,
                  paperSize: config.paperSize
                })
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="generic80mm">Generic 80mm (40 chars)</SelectItem>
                <SelectItem value="epson80mm">Epson 80mm (42 chars)</SelectItem>
                <SelectItem value="star80mm">Star 80mm (48 chars)</SelectItem>
                <SelectItem value="compact80mm">Compact 80mm (38 chars)</SelectItem>
                <SelectItem value="narrow80mm">Narrow 80mm (36 chars)</SelectItem>
                <SelectItem value="generic58mm">Generic 58mm (32 chars)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select your printer model for optimal formatting
            </p>
          </div>

          {/* Character Width (Advanced) */}
          <div className="grid gap-2">
            <Label>Character Width (Advanced)</Label>
            <Select
              value={settings.charactersPerLine?.toString() || '40'}
              onValueChange={(value) => 
                setSettings({ ...settings, charactersPerLine: parseInt(value) })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="32">32 characters</SelectItem>
                <SelectItem value="36">36 characters</SelectItem>
                <SelectItem value="38">38 characters</SelectItem>
                <SelectItem value="40">40 characters (Default)</SelectItem>
                <SelectItem value="42">42 characters</SelectItem>
                <SelectItem value="44">44 characters</SelectItem>
                <SelectItem value="48">48 characters</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Adjust if text wraps on your printer. Lower values = narrower receipts
            </p>
          </div>

          {/* Paper Size */}
          <div className="grid gap-2">
            <Label>Paper Size</Label>
            <Select
              value={settings.paperSize}
              onValueChange={(value: '80mm' | '58mm' | '44mm') => 
                setSettings({ ...settings, paperSize: value })
              }
              disabled
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="80mm">80mm (Standard)</SelectItem>
                <SelectItem value="58mm">58mm (Compact)</SelectItem>
                <SelectItem value="44mm">44mm (Mini)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Paper size is set by printer type
            </p>
          </div>

          {/* Number of Copies */}
          <div className="grid gap-2">
            <Label>Number of Copies</Label>
            <Select
              value={settings.copies.toString()}
              onValueChange={(value) => setSettings({ ...settings, copies: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Preview Mode */}
          <div className="flex items-center justify-between">
            <Label htmlFor="preview" className="flex flex-col gap-1">
              <span>Preview before printing</span>
              <span className="text-xs text-muted-foreground font-normal">
                Show receipt preview before sending to printer
              </span>
            </Label>
            <Switch
              id="preview"
              checked={settings.enablePreview}
              onCheckedChange={(checked) => setSettings({ ...settings, enablePreview: checked })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}